import { Fixture } from '../types';

/**
 * 在原图上绘制半透明的彩色扇形引导色块（预处理涂抹引导）
 * 这个方法会在原图上直接绘制彩色扇形，让 AI 看到图片中有颜色，更容易理解要在这里生成光
 * @param base64Image - 原始图片的 Base64 字符串
 * @param fixtures - 光源数据数组
 * @param imgWidth - 图片宽度
 * @param imgHeight - 图片高度
 * @returns 处理后的图片 Base64 字符串
 */
export function prePaintLightGuides(
  base64Image: string,
  fixtures: Fixture[],
  imgWidth: number,
  imgHeight: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      console.log('[预处理涂抹] 开始在原图上绘制引导色块...');
      console.log(`[预处理涂抹] 图片尺寸: ${imgWidth}x${imgHeight}`);
      
      // 过滤出需要处理的 spotlight
      const spotlights = fixtures.filter(f => f.type === 'spotlight' && f.isModified);
      console.log(`[预处理涂抹] Spotlight数量: ${spotlights.length}`);
      
      if (spotlights.length === 0) {
        console.log('[预处理涂抹] 没有需要处理的spotlight，直接返回原图');
        resolve(base64Image);
        return;
      }
      
      // 创建 Canvas
      const canvas = document.createElement('canvas');
      canvas.width = imgWidth;
      canvas.height = imgHeight;
      const ctx0 = canvas.getContext('2d');
      if (!ctx0) {
        throw new Error('无法创建Canvas上下文');
      }
      let ctx: CanvasRenderingContext2D = ctx0;
      
      // 加载原图
      const img = new Image();
      img.onload = () => {
        try {
          // 🔥 关键修复：使用图片的真实尺寸，而不是传入的尺寸
          // 如果传入的尺寸与图片真实尺寸不匹配，使用图片真实尺寸
          const actualWidth = img.naturalWidth || imgWidth;
          const actualHeight = img.naturalHeight || imgHeight;
          
          // 🔥 如果尺寸不匹配，更新 Canvas 尺寸
          let finalWidth = imgWidth;
          let finalHeight = imgHeight;
          if (actualWidth !== imgWidth || actualHeight !== imgHeight) {
            console.warn(`[预处理涂抹] ⚠️  尺寸不匹配！传入尺寸: ${imgWidth}x${imgHeight}, 图片真实尺寸: ${actualWidth}x${actualHeight}`);
            console.warn(`[预处理涂抹] 🔧 使用图片真实尺寸: ${actualWidth}x${actualHeight}`);
            canvas.width = actualWidth;
            canvas.height = actualHeight;
            // 重新获取上下文（Canvas 尺寸改变后需要重新获取）
            const newCtx = canvas.getContext('2d');
            if (newCtx) {
              // 替换上下文引用
              ctx = newCtx;
            }
            // 更新尺寸变量
            finalWidth = actualWidth;
            finalHeight = actualHeight;
            console.log(`[预处理涂抹] ✅ Canvas 尺寸已更新: ${imgWidth}x${imgHeight} -> ${finalWidth}x${finalHeight}`);
          }
          
          // 1. 先绘制原图（使用图片真实尺寸）
          ctx.drawImage(img, 0, 0, finalWidth, finalHeight);
          console.log('[预处理涂抹] ✅ 原图已绘制到Canvas，尺寸:', finalWidth, 'x', finalHeight);
          
          // 2. 为每个spotlight绘制半透明的彩色扇形引导色块
          // 🔥 已禁用：不在网页端显示红色/绿色角度范围框
          // spotlights.forEach((fixture, index) => {
          if (false) { // 🔥 禁用绘制角度范围框
          spotlights.forEach((fixture, index) => {
            const x = (fixture.x / 1000) * finalWidth;
            const y = (fixture.y / 1000) * finalHeight;
            const rotation = fixture.rotation ?? 90;
            const beamAngle = fixture.beamAngle ?? 30;
            const intensity = fixture.intensity ?? 1;
            const hue = fixture.hue ?? 0;
            const colorTemp = fixture.colorTemp ?? 0;
            
            // 🔥 核心绘图逻辑：根据光束角度选择颜色
            // 红色代表 <=15度（极窄光束），绿色代表宽角（>15度）
            // 使用不透明度 0.95 的纯色块，取消渐变
            const alphaValue = 0.95; // 95% 不透明度，确保 AI 能清楚看到引导色块
            const isNarrowBeam = beamAngle <= 15;
            
            // 🔥 根据光束角度选择颜色：红色 = 窄光束（<=15°），绿色 = 宽角（>15°）
            let guideColor: string;
            if (isNarrowBeam) {
              // 红色：极窄光束（5°-15°）
              guideColor = `rgba(255, 0, 0, ${alphaValue})`; // 纯红色，95% 不透明度
              console.log(`[预处理涂抹] 窄光束（${beamAngle}°），使用红色引导`);
            } else {
              // 绿色：宽角光束（>15°）
              guideColor = `rgba(0, 255, 0, ${alphaValue})`; // 纯绿色，95% 不透明度
              console.log(`[预处理涂抹] 宽角光束（${beamAngle}°），使用绿色引导`);
            }
            
            console.log(`[预处理涂抹] Spotlight ${index + 1}:`);
            console.log(`  - 位置: (${fixture.x}, ${fixture.y}) -> 像素坐标 (${x.toFixed(1)}, ${y.toFixed(1)})`);
            console.log(`  - 角度: rotation=${rotation}°, beamAngle=${beamAngle}°`);
            console.log(`  - 引导颜色: ${guideColor}`);
            
            // 计算扇形参数（与 maskGenerator.ts 保持一致）
            const rotationRad = (rotation * Math.PI) / 180;
            const halfBeamRad = (beamAngle * Math.PI) / 180 / 2;
            const startAngle = rotationRad - halfBeamRad;
            const endAngle = rotationRad + halfBeamRad;
            
            // 🔥 动态半径：根据角度动态调整引导半径
            // 窄光束（<=15°）需要更长的引导半径，宽角光束使用标准半径
            const diagonal = Math.sqrt(finalWidth * finalWidth + finalHeight * finalHeight);
            let radius: number;
            if (beamAngle <= 10) {
              // 极窄光束（<=10°）：延长 30%，提供更长的"轨道"引导
              radius = diagonal * 0.6 * 1.3;
              console.log(`[预处理涂抹] 极窄光束（${beamAngle}°），半径延长30%`);
            } else if (beamAngle <= 15) {
              // 窄光束（10°-15°）：延长 20%
              radius = diagonal * 0.6 * 1.2;
              console.log(`[预处理涂抹] 窄光束（${beamAngle}°），半径延长20%`);
            } else {
              // 宽角光束（>15°）：使用标准半径
              radius = diagonal * 0.6;
              console.log(`[预处理涂抹] 宽角光束（${beamAngle}°），使用标准半径`);
            }
            
            // 🔥 核心绘图逻辑：取消渐变，使用纯色块 + 3px 实线描边
            ctx.save();
            ctx.globalCompositeOperation = 'source-over'; // 覆盖模式
            
            // 1. 绘制纯色扇形（不透明度 0.95，无渐变）
            ctx.fillStyle = guideColor;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.arc(x, y, radius, startAngle, endAngle);
            ctx.closePath();
            ctx.fill();
            
            // 2. 添加 3px 实线描边以锐化边界
            ctx.strokeStyle = guideColor.replace(alphaValue.toString(), '1.0'); // 边缘完全不透明
            ctx.lineWidth = 3; // 3像素宽的实线描边
            ctx.lineCap = 'round'; // 圆角端点，更清晰
            ctx.lineJoin = 'round'; // 圆角连接
            
            // 绘制扇形边界（两条边 + 弧线）
            ctx.beginPath();
            // 第一条边（从中心到起始角度）
            ctx.moveTo(x, y);
            ctx.lineTo(
              x + Math.cos(startAngle) * radius,
              y + Math.sin(startAngle) * radius
            );
            // 弧线（从起始角度到结束角度）
            ctx.arc(x, y, radius, startAngle, endAngle);
            // 第二条边（从结束角度回到中心）
            ctx.lineTo(x, y);
            ctx.closePath();
            ctx.stroke();
            
            ctx.restore();
            
            console.log(`[预处理涂抹] ✅ Spotlight ${index + 1} 引导色块已绘制`);
          });
          } // 🔥 禁用绘制角度范围框的结束
          
          // 5. 将处理后的图片转换为 Base64
          // 🔥 关键修复：确保返回的图片格式符合后端验证要求（data:image/png;base64,...）
          let processedImage = canvas.toDataURL('image/png');
          
          // 🔥 验证格式：确保包含正确的前缀
          if (!processedImage.startsWith('data:image/png;base64,')) {
            console.error('[预处理涂抹] ❌ 图片格式不正确:', processedImage.substring(0, 50));
            // 如果格式不正确，尝试修复
            if (processedImage.startsWith('data:image/')) {
              // 如果已经有 data:image/ 前缀但格式不对，提取 base64 部分并重新构建
              const base64Data = processedImage.split(',')[1] || processedImage;
              processedImage = `data:image/png;base64,${base64Data}`;
            } else {
              // 如果没有前缀，添加完整前缀
              processedImage = `data:image/png;base64,${processedImage}`;
            }
            console.log('[预处理涂抹] 🔧 已修复图片格式');
          }
          
          console.log(`[预处理涂抹] ✅ 预处理完成，处理后图片大小: ${processedImage.length} 字符`);
          console.log(`[预处理涂抹] ✅ 图片格式验证: ${processedImage.substring(0, 22)}...`);
          
          // 可选：控制台调试用（不再向页面注入蓝色边框预览图）
          if (typeof window !== 'undefined') {
            (window as any).__lastPreprocessedImage = processedImage;
          }
          
          resolve(processedImage);
        } catch (error) {
          console.error('[预处理涂抹] ❌ 绘制引导色块失败:', error);
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        console.error('[预处理涂抹] ❌ 图片加载失败:', error);
        reject(new Error('图片加载失败'));
      };
      
      // 设置图片源
      img.src = base64Image;
    } catch (error) {
      console.error('[预处理涂抹] ❌ 预处理失败:', error);
      reject(error);
    }
  });
}
